import React from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import PropTypes from 'prop-types';
import vapi from '../../javascript/frontend/api/vapi';
import vahoy from '../../javascript/vahoy';

class FollowupAllModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      userId: props.userId,
      email: props.email,
      token: props.token,
      toSguid: props.toSguid,
      fields: {
        body: props.emailBody,
        subject: props.subject,
      },
      showPopup: props.showPopup,
      submitting: false,
      queuedMessage: null,
      queuedPath: null,
      error: null,
    };
    this.close = this.close.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.sendMessage = this.sendMessage.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (props.showPopup != state.showPopup) {
      return {
        showPopup: props.showPopup,
        fields: {
          body: props.emailBody,
          subject: props.subject,
        },
        submitting: false,
        queuedMessage: null,
        queuedPath: null,
        error: null,
      };
    }

    return {};
  }

  handleChange = (field, event) => {
    const { fields } = this.state;
    fields[field] = event.target.value;
    this.setState({
      fields,
    });
  };

  sendMessage = () => {
    const {
      closeModal,
    } = this.props;
    const {
      userId,
      email,
      token,
      toSguid,
      fields,
      submitting,
    } = this.state;
    const {
      subject,
      body,
    } = fields;

    // Guard against double-submit: a re-click while in flight re-sends the whole batch.
    if (submitting) { return; }

    this.setState({ submitting: true, error: null });

    vapi.followUpMessage(userId, email, token, toSguid, subject, body)
      .then((response) => {
        // Restricted (disabled_interactions) account: the API responds 200 with a URL;
        // navigating there shows the "account temporarily disabled" notice (same flow as pitching).
        if (response?.data?.disabledOrRestricted) {
          window.location.assign(response.data.location);
          return;
        }
        // Large batches are delivered asynchronously by V2; switch the modal to its queued state
        // instead of reloading (submitting stays true so Send can't re-fire the batch).
        if (response?.data?.queued) {
          this.setState({ queuedMessage: response.data.success, queuedPath: response.data.path });
          return;
        }
        // Leave submitting=true: closeModal reloads, and reloading sooner would cancel the request.
        closeModal(true);
      }).catch((error) => {
        this.setState({ submitting: false, error: 'Your message could not be sent. Please try again.' });
        return error;
      });

    vahoy.track('FollowupAllModal#sendMessage');
  };

  close = () => {
    const { closeModal } = this.props;
    const { fields, queuedMessage } = this.state;
    const { body } = fields;

    // The batch is queued server-side: nothing to lose (skip the confirm), and nothing on the
    // page has changed yet (skip the reload the sync path needs).
    if (queuedMessage) {
      closeModal(false);
      return;
    }

    if (body) {
      if (window.confirm('You will lose your message?')) {
        closeModal(false);
      }
    } else {
      closeModal(false);
    }
  };

  render() {
    const {
      fields,
      showPopup,
      submitting,
      queuedMessage,
      queuedPath,
      error,
    } = this.state;
    const {
      subject,
      body,
    } = fields;

    const closeBtn = (
      <button type="button" className="btn-close" aria-label="Close" onClick={this.close} />
    );

    /* eslint-disable react/no-danger */
    return (
      <Modal className="modal-lg" isOpen={showPopup} toggle={this.close} backdrop="static">
        <ModalHeader toggle={this.close} close={closeBtn} className="quick-reply-modal-header">
          <div className="row">
            <div className="col-12">
              Reply to All
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="row">
            <div className="col-12">
              <div className="alert alert-light-purple">
                <i className="fa-regular fa-circle-info header" />&nbsp;<strong>Note:</strong> Qwoted recommends scheduling a video interview to validate expertise.
              </div>
            </div>

            {error && (
              <div className="col-12">
                <div className="alert alert-danger" role="alert">{error}</div>
              </div>
            )}

            {queuedMessage && (
              <div className="col-12">
                <div className="alert alert-success" role="status">{queuedMessage}</div>
                <p className="small text-muted mb-2">
                  Experts who already received this exact message are skipped automatically — no
                  one gets a duplicate.
                </p>
              </div>
            )}

            <label className="col-2 col-form-label">Subject</label>
            <div className="col-10 mb-3">
              <input
                id="subject"
                className="form-control"
                type="subject"
                value={subject}
                onChange={this.handleChange.bind(this, 'subject')}
                disabled
              />
            </div>

            <div className="col-12">
              <textarea
                id="email-body"
                className="form-control"
                name="email-body"
                placeholder="Your message"
                value={body}
                onChange={this.handleChange.bind(this, 'body')}
                rows="10"
                required
                disabled={!!queuedMessage}
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          {queuedMessage ? (
            <>
              {queuedPath && (
                <Button outline color="secondary" tag="a" href={queuedPath}>View inbox</Button>
              )}
              <Button color="primary" onClick={this.close}>Done</Button>
            </>
          ) : (
            <>
              <Button color="primary" onClick={this.sendMessage} disabled={submitting}>
                {submitting ? 'Sending…' : 'Send'}
              </Button>
              <Button onClick={this.close} disabled={submitting}>Close</Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    );
    /* eslint-enable react/no-danger */
  }
}

FollowupAllModal.propTypes = {
  userId: PropTypes.number,
  email: PropTypes.string,
  token: PropTypes.string,
  toSguid: PropTypes.array,
  emailBody: PropTypes.string,
  subject: PropTypes.string,
  showPopup: PropTypes.bool,
  closeModal: PropTypes.func.isRequired,
};

FollowupAllModal.defaultProps = {
  userId: undefined,
  email: undefined,
  token: undefined,
  toSguid: undefined,
  emailBody: undefined,
  subject: undefined,
  showPopup: undefined,
};

export default FollowupAllModal;
